// ============================================================
// TMR Portal Admin — picking an Empresa's managers from real internal users
// ============================================================
import { useState } from 'react';
import { Avatar, Btn } from '../../components/primitives';
import { RecordPicker } from '../../components/RecordPicker';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useCompanyManagerSearch } from '../../data/companies/queries';
import type { CompanyManagerOption } from '../../data/companies/contract';
import type { CompanyForm } from '../../data/companies/form';
import type { SetField } from './steps';

/** Which of the two manager roles a field stands for. */
export type ManagerRole = 'sales' | 'postSales';

/**
 * Everything that differs between the two roles, in one place.
 *
 * The roles are identical in mechanics — same directory, same picker, same optionality — and differ only
 * in the words and in which pair of form fields they write. Keeping that difference as data rather than as
 * two components is what stops the second one from quietly drifting from the first.
 */
const ROLES: Record<ManagerRole, {
  label: string;
  subtitle: string;
  idField: 'salesManagerUserId' | 'postSalesManagerUserId';
  nameField: 'salesManagerName' | 'postSalesManagerName';
}> = {
  sales: {
    label: 'Gestor de venda',
    subtitle: 'Quem vendeu esta empresa. É o gestor que o sistema legado conhece.',
    idField: 'salesManagerUserId',
    nameField: 'salesManagerName',
  },
  postSales: {
    label: 'Gestor de pós-venda',
    subtitle: 'Quem cuida desta empresa depois da primeira venda.',
    idField: 'postSalesManagerUserId',
    nameField: 'postSalesManagerName',
  },
};

/** Props for {@link CompanyManagerField}. */
interface CompanyManagerFieldProps {
  form: CompanyForm;
  set: SetField;
  /** Which manager this field edits. */
  role: ManagerRole;
}

/**
 * Chooses one of the Empresa's managers — Gestor de venda or Gestor de pós-venda (01 §2).
 *
 * The chosen id is what the cadastre submits; the name rides along only to show who is picked.
 *
 * The field carries **no permission gate of its own**, and that is the fix rather than an omission. It
 * used to require `users.manage` — the permission that creates operators, grants and revokes roles and
 * disables accounts — because the picker read `GET /users`; whoever merely registered an Empresa had to
 * hold all of that or lose the field. It now reads `GET /companies/managers`, gated by
 * `company.onboard`/`company.update`, which the form the field lives in already requires. There is no
 * remaining state where the form renders and the picker cannot be used.
 */
export function CompanyManagerField({ form, set, role }: CompanyManagerFieldProps) {
  const [picking, setPicking] = useState(false);
  const config = ROLES[role];
  const userId = form[config.idField];
  const name = form[config.nameField];

  const select = (user: CompanyManagerOption) => {
    set(config.idField, user.id);
    set(config.nameField, user.name);
  };

  const clear = () => {
    set(config.idField, '');
    set(config.nameField, '');
  };

  return (
    <>
      {userId ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px 7px 8px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)' }}>
          <Avatar name={name} size={30} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </span>
          <button type="button" onClick={() => setPicking(true)} className="pa-link-btn" style={{ fontSize: 12.5 }}>Trocar</button>
          <button type="button" onClick={clear} className="pa-icon-btn" style={{ width: 28, height: 28 }} title="Remover gestor">
            <StrokeIcon d={Icons.x} size={15} />
          </button>
        </div>
      ) : (
        <Btn variant="secondary" onClick={() => setPicking(true)}>
          <StrokeIcon d={Icons.users} size={15} /> Escolher gestor
        </Btn>
      )}

      {picking && (
        <RecordPicker<CompanyManagerOption>
          eyebrow={config.label}
          title="Escolher gestor"
          subtitle={config.subtitle}
          placeholder="Nome ou e-mail do colaborador…"
          useSearch={useCompanyManagerSearch}
          getKey={(user) => user.id}
          toRow={(user) => ({ primary: user.name, secondary: user.email })}
          onSelect={select}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}
